import {useEffect, useState} from 'react';
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import {getProductBreadcrumb, getProductStyles} from 'service/ProductService';
import {useQueryParams} from 'shared/helpers';
import {mapBreadcrumb} from 'hooks/Product/helpers/mapBreadcrumb';
import {useLazyGetCabinetDetailsQuery} from 'components/customer/Product/store/productApi';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {getPlaceholderDisplay} from 'components/customer/RoomPlanner/helpers/placeholder/editorParams';
import {getPlaceholderCatalogSubCategory} from 'components/customer/RoomPlanner/helpers/placeholder/catalog';

export interface BreadCrumbApi {
    id: number;
    name: string;
    category?: number;
}

export interface BreadCrumb {
    id?: number;
    key: string;
    link?: string;
}

export const useBreadCrumb = () => {
    const navigate = useNavigate();
    const {jobId, roomId} = useParams();
    const {product, category, subCategory, favourites} = useQueryParams() as {
        product: string;
        category: string;
        subCategory: string;
        favourites: string;
    };
    const [searchParams] = useSearchParams();
    const placeholderEdit = searchParams.get('placeholderEdit');
    const placeholderFace = searchParams.get('placeholderFace');
    const placeholderName = searchParams.get('placeholderName');
    const {selectedCabinetGroup} = useRoomPlannerContext();
    const placeholderDisplay = getPlaceholderDisplay(selectedCabinetGroup);
    const [breadcrumbs, setBreadcrumbs] = useState<BreadCrumb[]>([]);
    const location = useLocation();
    const [getCabinetDetails] = useLazyGetCabinetDetailsQuery();

    const addBreadCrumb = (item: BreadCrumb[]) => {
        if (Array.isArray(item)) setBreadcrumbs(item);
        else setBreadcrumbs([item]);
    };

    const removeBreadCrumb = (removeKey: string) => {
        const filteredBreadCrumb = breadcrumbs.filter(
            (item) => item.key !== removeKey
        );
        setBreadcrumbs(filteredBreadCrumb);

        if (filteredBreadCrumb.length) {
            void navigate(
                filteredBreadCrumb[filteredBreadCrumb.length - 1].link
            );
        } else {
            if (roomId) {
                void navigate(location.pathname);
            } else {
                void navigate(`/v2/job/${jobId}/hardware`);
            }
        }
    };

    const popBreadCrumb = (index?: number) => {
        if (typeof index !== 'undefined') breadcrumbs.length = index + 1;
        else breadcrumbs.pop();

        setBreadcrumbs(breadcrumbs);
    };

    const resetBreadCrumb = (items: BreadCrumb[] = []) => {
        setBreadcrumbs(items);
    };

    const fetchBreadcrumbs = async (
        product?: string,
        category?: string,
        subCategory?: string,
        favourites?: string
    ) => {
        if (favourites) {
            setBreadcrumbs([
                {
                    key: 'Favourites',
                },
            ]);
            return;
        }

        // Placeholders is not a product style id, so the breadcrumb API
        // returns nothing. Build All Placeholders | Placeholders [| sub-cat].
        if (category === 'placeholder') {
            const crumbs: BreadCrumb[] = [
                {
                    key: 'Placeholders',
                    link: `${location.pathname}?category=placeholder`,
                },
            ];
            const editorCategory =
                getPlaceholderCatalogSubCategory(subCategory) ||
                (placeholderEdit ? placeholderDisplay.category : '');
            const editorName =
                placeholderName ||
                (placeholderEdit ? placeholderDisplay.name : '');
            if (editorCategory) {
                crumbs.push({
                    key: editorCategory,
                    link: `${location.pathname}?category=placeholder&subCategory=${encodeURIComponent(
                        editorCategory
                    )}`,
                });
            }
            if ((placeholderEdit || placeholderFace) && editorName) {
                crumbs.push({
                    key: editorName,
                });
            }
            setBreadcrumbs(crumbs);
            return;
        }

        const productStyles = await getProductStyles();

        let productDetails;

        if (product) {
            productDetails = await getCabinetDetails(
                {
                    cabinetId: Number(product),
                },
                true
            ).unwrap();

            if (productDetails.style) {
                category = productDetails.style?.toString();
            }

            if (productDetails.sub_style_id) {
                subCategory = productDetails.sub_style_id?.toString();
            }
        }

        const breadCrumbs = await getProductBreadcrumb(
            category,
            subCategory,
            productStyles
        );

        if (category) {
            let mappedBreadCrumbs: BreadCrumb[] = [];

            if (roomId) {
                if (breadCrumbs.length > 0) {
                    mappedBreadCrumbs = mapBreadcrumb(
                        breadCrumbs,
                        Number(jobId),
                        Number(roomId),
                        location
                    );
                }
            } else {
                if (breadCrumbs.length > 1) {
                    mappedBreadCrumbs = mapBreadcrumb(
                        breadCrumbs,
                        Number(jobId),
                        Number(roomId),
                        location
                    );
                }
            }

            if (productDetails) {
                mappedBreadCrumbs.push({
                    key: productDetails.name,
                });
            }

            setBreadcrumbs(mappedBreadCrumbs);
        }
    };

    useEffect(() => {
        setBreadcrumbs([]);

        void fetchBreadcrumbs(product, category, subCategory, favourites);
    }, [
        product,
        category,
        subCategory,
        favourites,
        placeholderEdit,
        placeholderFace,
        placeholderName,
        placeholderDisplay.category,
        placeholderDisplay.name,
    ]);

    return {
        breadcrumbs,
        addBreadCrumb,
        removeBreadCrumb,
        popBreadCrumb,
        resetBreadCrumb,
    };
};
